'use client';

import {
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  Box,
  HTMLChakraComponents,
  SystemStyleObject,
  defineStyle,
  defineStyleConfig,
  forwardRef
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { ReactNode } from 'react';

import Icon, { IconKind, IconName } from '@/components/atomic/v1/icon/Icon';
import Spinner from '@/components/atomic/v1/spinner/Spinner';

const focusActiveStyles = defineStyle({
  textDecoration: 'underline',
  textUnderlineOffset: '0.25rem',
  outlineWidth: '0.125rem',
  outlineOffset: 2,
  outlineStyle: 'solid',
  outlineColor: 'button-primary/blue:active'
});

const baseStyles = defineStyle({
  cursor: 'pointer',
  borderRadius: '3.125rem',
  outlineOffset: '0',
  _disabled: {
    bg: 'button-primary/disabled',
    borderColor: 'button-primary/disabled',
    opacity: 1
  },
  _focusVisible: {
    boxShadow: 'none'
  }
});

// Overwrite the disabled styles because the isLoading button state UI is very different from the disabled state UI
// TODO: The styles in this component can be refactored to be more DRY but it will require a lot of updates to snapshots and testing
// The variants should be loosely coupled from the base styles as well as the Overlay component
const loadingStateStyles = defineStyle({
  _disabled: {
    cursor: 'not-allowed'
  },
  _hover: {
    _disabled: {}
  },
  _active: {
    ...focusActiveStyles,
    bg: ''
  },
  _focus: {
    ...focusActiveStyles,
    bg: ''
  }
});

const primaryVariant = defineStyle({
  color: 'button-primary/text',
  _hover: {
    bg: 'button-primary/blue:hover',
    _disabled: {
      bg: 'button-primary/disabled'
    }
  }
});

const tertiaryVariant = defineStyle({
  _hover: {
    bg: 'button-tertiary/blue:hover',
    _disabled: {
      bg: 'button-primary/disabled',
      color: 'button-tertiary/disabled'
    }
  }
});

const primaryBlueStyles = defineStyle({
  bg: 'button-primary/blue:default',
  _hover: {
    bg: 'button-primary/blue:hover',
    _disabled: {
      bg: 'button-primary/disabled'
    }
  },
  _active: {
    ...focusActiveStyles,
    bg: 'button-primary/blue:active'
  },
  _focus: {
    ...focusActiveStyles,
    bg: 'button-primary/blue:active'
  }
});

const primaryOrangeStyles = defineStyle({
  background: 'button-primary/orange:default',
  _hover: {
    bg: 'button-primary/orange:hover',
    _disabled: {
      bg: 'button-primary/disabled'
    }
  },
  _active: {
    ...focusActiveStyles,
    bg: 'button-primary/orange:active'
  },
  _focus: {
    ...focusActiveStyles,
    bg: 'button-primary/orange:active'
  }
});

const tertiaryBlueStyles = defineStyle({
  color: 'button-tertiary/blue:text',
  _disabled: {
    bg: 'white',
    color: 'button-tertiary/disabled',
    cursor: 'not-allowed'
  },
  _hover: {
    bg: 'button-tertiary/blue/bg:hover',
    color: 'button-tertiary/blue:hover',
    _disabled: {
      bg: 'white',
      color: 'button-tertiary/disabled',
      borderColor: 'white'
    }
  },
  _active: {
    ...focusActiveStyles,
    bg: 'button-tertiary/blue/bg:hover',
    color: 'button-tertiary/blue:hover',
    '> div': {
      borderColor: 'button-secondary/blue:hover'
    }
  },
  _focus: {
    ...focusActiveStyles,
    bg: 'button-tertiary/blue/bg:hover',
    color: 'button-tertiary/blue:hover',
    '> div': {
      borderColor: 'button-secondary/blue:hover'
    }
  }
});

const tertiaryOrangeStyles = defineStyle({
  color: 'button-tertiary/orange:text',
  _disabled: {
    bg: 'white',
    color: 'button-tertiary/disabled',
    cursor: 'not-allowed'
  },
  _hover: {
    bg: 'button-tertiary/orange/bg:hover',
    color: 'button-tertiary/orange:hover',
    _disabled: {
      bg: 'white',
      color: 'button-tertiary/disabled',
      borderColor: 'white'
    }
  },
  _active: {
    ...focusActiveStyles,
    bg: 'button-tertiary/orange/bg:hover',
    color: 'button-tertiary/orange:hover'
  },
  _focus: {
    ...focusActiveStyles,
    bg: 'button-tertiary/orange/bg:hover',
    color: 'button-tertiary/orange:hover'
  }
});

const textLinkStyles = defineStyle({
  fontSize: 'regular',
  fontWeight: 'regular',
  padding: 0,
  color: 'button-tertiary/blue:text',
  _disabled: {
    color: 'button-tertiary/disabled',
    cursor: 'not-allowed'
  },
  _hover: {
    textDecoration: 'underline',
    textUnderlineOffset: '0.25rem',
    color: 'button-tertiary/blue:hover',
    _disabled: {
      color: 'button-tertiary/disabled',
      borderColor: 'white'
    }
  },
  _active: {
    ...focusActiveStyles,
    color: 'button-tertiary/blue:hover',
    '> div': {
      borderColor: 'button-secondary/blue:hover'
    },
    borderRadius: '2px'
  },
  _focus: {
    ...focusActiveStyles,
    color: 'button-tertiary/blue:hover',
    '> div': {
      borderColor: 'button-secondary/blue:hover'
    },
    borderRadius: '2px'
  }
});

function getVariantStyles(variant: ButtonVariants, color: ButtonColor, isDisabled: boolean, isLoading: boolean) {
  if (variant === 'primary' || (variant === 'secondary' && isDisabled)) {
    if (color === 'blue') {
      return isLoading ? { ...primaryBlueStyles, ...loadingStateStyles } : primaryBlueStyles;
    }

    if (color === 'orange') {
      return isLoading ? { ...primaryOrangeStyles, ...loadingStateStyles } : primaryOrangeStyles;
    }
  }

  if (variant === 'tertiary' || variant === 'secondary') {
    if (color === 'blue') {
      return isLoading ? { ...tertiaryBlueStyles, ...loadingStateStyles } : tertiaryBlueStyles;
    }

    if (color === 'orange') {
      return isLoading ? { ...tertiaryOrangeStyles, ...loadingStateStyles } : tertiaryOrangeStyles;
    }
  }

  if (variant === 'textLink') {
    return textLinkStyles;
  }

  return isLoading ? { ...primaryBlueStyles, ...loadingStateStyles } : primaryBlueStyles;
}

function isIconOnlyButton(text?: string, children?: ReactNode, leftIconName?: IconName, rightIconName?: IconName) {
  return !text && !children && (leftIconName || rightIconName);
}

function getConditionalStyles(
  text?: string,
  children?: ReactNode,
  leftIconName?: IconName,
  rightIconName?: IconName,
  size?: ButtonSize
) {
  let styles: SystemStyleObject = {};

  if (isIconOnlyButton(text, children, leftIconName, rightIconName)) {
    styles = {
      px: 3
    };
  } else if (size === 'small') {
    styles = {
      px: 4
    };
  } else if (size === 'regular') {
    styles = {
      px: 8,
      minW: '180px'
    };
  }

  return styles;
}

export interface ButtonProps
  extends Pick<ChakraButtonProps, 'isDisabled' | 'onClick' | 'isLoading' | 'loadingText' | 'spinnerPlacement'> {
  text?: string;
  children?: ReactNode;
  variant?: ButtonVariants;
  leftIconName?: IconName | undefined;
  rightIconName?: IconName | undefined;
  iconKind?: IconKind;
  color?: ButtonColor;
  href?: string | undefined;
  size?: ButtonSize;
  isExternal?: boolean;
  sx?: SystemStyleObject;
}

type ButtonVariants = 'primary' | 'secondary' | 'tertiary' | 'textLink';
export type ButtonColor = 'blue' | 'orange';
export type ButtonSize = 'regular' | 'small';

export const buttonTheme = defineStyleConfig({
  baseStyle: baseStyles,
  variants: {
    primary: primaryVariant,
    secondary: tertiaryVariant,
    tertiary: tertiaryVariant,
    textLink: textLinkStyles
  },
  defaultProps: {
    variant: 'primary'
  }
});

const Overlay = ({ color }: { color: ButtonColor }) => (
  <Box
    as="span"
    pos="absolute"
    bg="transparent"
    w="100%"
    h="100%"
    borderRadius="50px"
    borderColor={`button-secondary/${color}:border`}
    borderStyle="solid"
    borderWidth="0.125rem"
    _hover={{ borderColor: `button-secondary/${color}:hover` }}
    pointerEvents="none"
  />
);

/**
 * Based on `<Button>` from ChakraUI component library
 *
 * https://chakra-ui.com/docs/components/button
 */
const Button = forwardRef<ButtonProps, HTMLChakraComponents['button']>(
  (
    {
      text,
      children,
      variant = 'primary',
      color = 'blue',
      size = 'regular',
      isDisabled = false,
      isLoading = false,
      spinnerPlacement = 'start',
      loadingText,
      leftIconName,
      rightIconName,
      iconKind = 'outline',
      href,
      isExternal,
      sx = {},
      ...rest
    },
    ref
  ) => {
    const conditionalStyles = getConditionalStyles(text, children, leftIconName, rightIconName, size);
    const conditionalProps: Pick<ChakraButtonProps, 'rightIcon' | 'leftIcon'> = {};

    if (rightIconName) {
      conditionalProps.rightIcon = <Icon name={rightIconName} size={size === 'regular' ? 24 : 18} kind={iconKind} />;
    }

    if (leftIconName) {
      conditionalProps.leftIcon = <Icon name={leftIconName} size={size === 'regular' ? 24 : 18} kind={iconKind} />;
    }

    type LinkProps = {
      as: typeof NextLink;
      href: string;
      target: string | undefined;
    };

    const linkProps: LinkProps = href
      ? {
          as: NextLink,
          href,
          target: isExternal ? '_blank' : undefined
        }
      : ({} as LinkProps);

    return (
      <ChakraButton
        ref={ref}
        size={size === 'regular' ? 'lg' : 'sm'}
        textAlign="center"
        py={3}
        height="fit-content"
        whiteSpace="initial"
        isDisabled={isDisabled}
        isLoading={isLoading}
        loadingText={
          variant === 'secondary' ? (
            <>
              {loadingText}
              <Overlay color={color} />
            </>
          ) : (
            loadingText
          )
        }
        spinner={<Spinner variant="isLoading" size="small" />}
        display="inline-flex"
        justifyContent="center"
        alignItems="center"
        iconSpacing={0}
        columnGap={2}
        spinnerPlacement={spinnerPlacement}
        {...getVariantStyles(variant, color, isDisabled, isLoading)}
        sx={{ ...conditionalStyles, ...sx }}
        {...conditionalProps}
        {...linkProps}
        data-testid={`button-${text ?? children ?? href}`}
        {...rest}
      >
        {text || children}
        {variant === 'secondary' && !isDisabled && <Overlay color={color} />}
      </ChakraButton>
    );
  }
);

export default Button;
